import React, { useEffect, useState, useCallback } from 'react';
import { vehicles as vehiclesApi, extractError } from '../services/api';
import { VehicleStatusBadge } from '../components/ui/StatusBadge';
import { Modal, ConfirmDialog } from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/LoadingSpinner';
import { useToast } from '../store/ToastContext';
import { useAuth } from '../store/AuthContext';

const TYPES    = ['day_cab','sleeper','straight_truck','box_truck','flatbed','reefer'];
const STATUSES = ['available','in_use','maintenance','out_of_service'];
const FUELS    = ['diesel','gasoline','cng','electric'];

const BLANK = {
  unit_number: '', vin: '', year: '', make: '', model: '', type: 'day_cab', fuel_type: 'diesel',
  fuel_tank_capacity_gallons: '', avg_mpg: '', gross_vehicle_weight_lbs: '',
  license_plate: '', license_state: '', status: 'available', odometer_miles: '0',
  last_maintenance_date: '', next_maintenance_due: '', color: '', notes: '',
};

function VehicleForm({ initial, onSave, onClose, isEdit }) {
  const toast = useToast();
  const [form, setForm] = useState({ ...BLANK, ...initial });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({...e,[k]:undefined})); };

  const validate = () => {
    const e = {};
    if (!form.unit_number.trim()) e.unit_number = 'Required';
    if (!form.vin.trim())         e.vin = 'Required';
    if (!form.type)               e.type = 'Required';
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try { await onSave(form); }
    catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="label">Unit # *</label>
          <input className={`input-field ${errors.unit_number ? 'input-error' : ''}`} value={form.unit_number} onChange={e => set('unit_number', e.target.value)} placeholder="TRK-001" />
          {errors.unit_number && <p className="text-red-500 text-xs mt-1">{errors.unit_number}</p>}
        </div>
        <div className="col-span-2">
          <label className="label">VIN *</label>
          <input maxLength={17} className={`input-field ${errors.vin ? 'input-error' : ''}`} value={form.vin} onChange={e => set('vin', e.target.value.toUpperCase())} placeholder="1XKWDB0X12J123456" />
          {errors.vin && <p className="text-red-500 text-xs mt-1">{errors.vin}</p>}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div>
          <label className="label">Year</label>
          <input type="number" min="2000" max="2030" className="input-field" value={form.year} onChange={e => set('year', e.target.value)} />
        </div>
        <div>
          <label className="label">Make</label>
          <input className="input-field" value={form.make} onChange={e => set('make', e.target.value)} placeholder="Kenworth" />
        </div>
        <div>
          <label className="label">Model</label>
          <input className="input-field" value={form.model} onChange={e => set('model', e.target.value)} placeholder="T680" />
        </div>
        <div>
          <label className="label">Color</label>
          <input className="input-field" value={form.color || ''} onChange={e => set('color', e.target.value)} placeholder="White" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="label">Type *</label>
          <select className={`input-field ${errors.type ? 'input-error' : ''}`} value={form.type} onChange={e => set('type', e.target.value)}>
            {TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g,' ')}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Fuel Type</label>
          <select className="input-field" value={form.fuel_type} onChange={e => set('fuel_type', e.target.value)}>
            {FUELS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Status</label>
          <select className="input-field" value={form.status} onChange={e => set('status', e.target.value)}>
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <div>
          <label className="label">Tank (gal)</label>
          <input type="number" className="input-field" value={form.fuel_tank_capacity_gallons} onChange={e => set('fuel_tank_capacity_gallons', e.target.value)} />
        </div>
        <div>
          <label className="label">Avg MPG</label>
          <input type="number" step="0.1" className="input-field" value={form.avg_mpg} onChange={e => set('avg_mpg', e.target.value)} />
        </div>
        <div>
          <label className="label">GVW (lbs)</label>
          <input type="number" className="input-field" value={form.gross_vehicle_weight_lbs} onChange={e => set('gross_vehicle_weight_lbs', e.target.value)} />
        </div>
        <div>
          <label className="label">Odometer</label>
          <input type="number" className="input-field" value={form.odometer_miles} onChange={e => set('odometer_miles', e.target.value)} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">License Plate</label>
          <input className="input-field" value={form.license_plate || ''} onChange={e => set('license_plate', e.target.value)} />
        </div>
        <div>
          <label className="label">License State</label>
          <input maxLength={2} className="input-field" value={form.license_state || ''} onChange={e => set('license_state', e.target.value.toUpperCase())} placeholder="TX" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Last Maintenance</label>
          <input type="date" className="input-field" value={form.last_maintenance_date || ''} onChange={e => set('last_maintenance_date', e.target.value)} />
        </div>
        <div>
          <label className="label">Next Maintenance Due</label>
          <input type="date" className="input-field" value={form.next_maintenance_due || ''} onChange={e => set('next_maintenance_due', e.target.value)} />
        </div>
      </div>
      <div>
        <label className="label">Notes</label>
        <textarea rows={2} className="input-field resize-none" value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button onClick={onClose} className="btn-secondary">Cancel</button>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Vehicle'}
        </button>
      </div>
    </div>
  );
}

export function VehiclesPage() {
  const { hasRole } = useAuth();
  const toast = useToast();
  const [list, setList]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFil, setStatus]  = useState('all');
  const [typeFil, setType]      = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editVeh, setEdit]      = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const canManage = hasRole(['administrator', 'fleet_manager']);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFil !== 'all') params.status = statusFil;
      if (typeFil   !== 'all') params.type   = typeFil;
      const result = await vehiclesApi.list(params);
      setList(Array.isArray(result) ? result : result?.data || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFil, typeFil, toast]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    const v = await vehiclesApi.create(form);
    setList(prev => [v, ...prev]);
    setShowForm(false);
    toast.success(`Vehicle ${v.unit_number} added`);
  };

  const handleUpdate = async (form) => {
    const v = await vehiclesApi.update(editVeh.id, form);
    setList(prev => prev.map(x => x.id === v.id ? v : x));
    setEdit(null);
    toast.success('Vehicle updated');
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await vehiclesApi.delete(confirmDel.id);
      setList(prev => prev.filter(v => v.id !== confirmDel.id));
      toast.success('Vehicle marked out of service');
      setConfirmDel(null);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDeleting(false);
    }
  };

  const filtered = list.filter(v => {
    if (statusFil !== 'all' && v.status !== statusFil) return false;
    if (typeFil   !== 'all' && v.type   !== typeFil)   return false;
    if (search) {
      const s = search.toLowerCase();
      return v.unit_number?.toLowerCase().includes(s) ||
             v.vin?.toLowerCase().includes(s) ||
             v.make?.toLowerCase().includes(s) ||
             v.model?.toLowerCase().includes(s);
    }
    return true;
  });

  const maintenanceSoon = (date) => {
    if (!date) return false;
    const diff = (new Date(date) - new Date()) / (1000*60*60*24);
    return diff >= 0 && diff <= 30;
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Vehicles</h1>
            <p className="text-sm text-gray-500">{filtered.length} vehicle{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          {canManage && (
            <button onClick={() => setShowForm(true)} className="btn-primary">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Vehicle
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" className="input-field pl-9 py-2" placeholder="Search vehicles…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="flex gap-1 flex-wrap">
            {['all', ...STATUSES].map(s => (
              <button key={s} onClick={() => setStatus(s)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${statusFil === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {s === 'all' ? 'All Status' : s.replace(/_/g,' ')}
              </button>
            ))}
          </div>
          <div className="flex gap-1 flex-wrap">
            {['all', ...TYPES].map(t => (
              <button key={t} onClick={() => setType(t)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${typeFil === t ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {t === 'all' ? 'All Types' : t.replace(/_/g,' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="card overflow-hidden">
          {loading ? <TableSkeleton rows={8} cols={7} /> : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">No vehicles found</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="table-header">Unit</th>
                  <th className="table-header">Vehicle</th>
                  <th className="table-header">Type</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Odometer</th>
                  <th className="table-header">Fuel</th>
                  <th className="table-header">Next Maint.</th>
                  {canManage && <th className="table-header">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(v => (
                  <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                    <td className="table-cell font-bold text-gray-900">{v.unit_number}</td>
                    <td className="table-cell">
                      <div className="font-medium">{v.year} {v.make} {v.model}</div>
                      <div className="text-xs text-gray-500 font-mono">{v.vin}</div>
                    </td>
                    <td className="table-cell capitalize">{v.type?.replace(/_/g,' ')}</td>
                    <td className="table-cell"><VehicleStatusBadge status={v.status} /></td>
                    <td className="table-cell">{v.odometer_miles ? parseInt(v.odometer_miles).toLocaleString() + ' mi' : '—'}</td>
                    <td className="table-cell">
                      <div>{v.fuel_type}</div>
                      <div className="text-xs text-gray-500">{v.avg_mpg ? `${v.avg_mpg} mpg` : '—'}</div>
                    </td>
                    <td className={`table-cell ${maintenanceSoon(v.next_maintenance_due) ? 'text-amber-600 font-medium' : ''}`}>
                      {v.next_maintenance_due ? new Date(v.next_maintenance_due).toLocaleDateString() : '—'}
                      {maintenanceSoon(v.next_maintenance_due) && <span className="text-xs block">⚠ Due soon</span>}
                    </td>
                    {canManage && (
                      <td className="table-cell">
                        <div className="flex gap-1">
                          <button onClick={() => setEdit(v)} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 font-medium transition-colors">Edit</button>
                          {v.status !== 'out_of_service' && (
                            <button onClick={() => setConfirmDel(v)} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors">OOS</button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Add New Vehicle" size="lg">
        <VehicleForm initial={BLANK} onSave={handleCreate} onClose={() => setShowForm(false)} isEdit={false} />
      </Modal>
      {editVeh && (
        <Modal isOpen onClose={() => setEdit(null)} title="Edit Vehicle" size="lg">
          <VehicleForm initial={editVeh} onSave={handleUpdate} onClose={() => setEdit(null)} isEdit />
        </Modal>
      )}
      <ConfirmDialog isOpen={!!confirmDel} onClose={() => setConfirmDel(null)} onConfirm={handleDelete}
        title="Mark Out of Service" message={`Mark ${confirmDel?.unit_number} as out of service?`}
        confirmText="Mark OOS" variant="danger" isLoading={deleting} />
    </div>
  );
}
